import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Zap,
  Settings,
  Eye,
  BarChart3,
  CheckCircle2,
  Smartphone,
  Crown,
  Package,
  Users,
  Monitor,
  ArrowRight
} from 'lucide-react';

const FestivalsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const festivalNeeds = [
    { icon: Zap, text: 'Hurtig betaling' },
    { icon: Settings, text: 'Stabil drift' },
    { icon: Eye, text: 'Overblik over salg' },
    { icon: BarChart3, text: 'Fleksibel opsætning' },
  ];

  const festivalFeatures = [
  {
    icon: Smartphone,
    title: 'Fast Track',
    description: 'Gæster køber direkte i appen og henter ordren ved baren – uden at stå i kø.',
  },
  {
    icon: Crown,
    title: 'VIP bordbestilling',
    description: 'Bestil direkte fra bordet via mobilen – ideelt til VIP-områder og bordservering.',
  },
  {
    icon: Package,
    title: 'Lagerstyring',
    description: 'Live overblik over lager på tværs af barer og salgssteder.',
  },
  {
    icon: Users,
    title: 'Frivilligstyring',
    description: 'Administrer frivillige, vagter og roller i ét system.',
  },
  {
    icon: Monitor,
    title: 'Udleveringssystem',
    description: 'Touch-skærme i barerne sikrer hurtig og mere fejlfri udlevering.',
  },
  {
    icon: Settings,
    title: 'Multi-bar management',
    description: 'Opsæt og styr mange salgssteder fra ét centralt system.',
  },
];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero with problem statement */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h1 className="font-heading font-bold text-foreground mb-5 text-4xl sm:text-5xl lg:text-6xl">
                Bygget til festivaler
              </h1>
              <p className="text-lg text-body-text leading-relaxed">
                Festivaler stiller særlige krav til betalingssystemer og drift. Mange gæster, mange salgssteder og kortvarige peak-perioder kræver stabile løsninger. MUSEIQ er udviklet med netop dette i fokus.
              </p>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden shadow-large">
                <img
                  src="https://images.unsplash.com/photo-1685213400738-2841133e840d?w=800&q=80"
                  alt="Festival scene med publikum"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What festivals need */}
      <section className="py-20 md:py-28 bg-section-bg">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              Det tilbyder vi festivaler
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {festivalNeeds.map((need, index) => {
              const Icon = need.icon;
              return (
                <Card
                  key={index}
                  data-testid={`festival-need-${index}`}
                  className="group border-transparent hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(11,31,59,0.12)]"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-accent-purple/10 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6 text-accent-purple" />
                    </div>
                    <span className="font-heading font-semibold text-foreground">
                      {need.text}
                    </span>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform features for festivals */}
      <section className="py-20 md:py-28">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              I vores software finder du features som
            </h2>
            <p className="text-body-text text-lg">
              Inspireret af erfaringerne fra danske festivaler har vi bygget værktøjer der løser de mest almindelige driftsproblemer.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {festivalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  data-testid={`festival-feature-${index}`}
                  className="group border-transparent hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(11,31,59,0.12)]"
                >
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-lg bg-accent-blue/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5 text-accent-blue" />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-body-text leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works - flow */}
      <section className="py-20 md:py-28 bg-section-bg">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-large">
                <img
                  src="https://images.unsplash.com/photo-1695128708405-f9a4b1b6435f?w=800&q=80"
                  alt="Festival bar om aftenen"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Hvordan det fungerer
              </h2>
              <p className="text-body-text mb-6 text-lg">
                MUSEIQ er designet så både arrangører, barfolk og frivillige hurtigt kan komme i gang - med minimal oplæring.
              </p>
              <div className="space-y-4">
                {[
                  'Opsæt salgssteder, produkter og priser i systemet',
                  'Tildel frivillige og personale til barer og vagter',
                  'Gæster betaler via terminal eller app',
                  'Følg salget live og juster undervejs',
                  'Analysér eventet bagefter med data og rapporter'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-accent-purple/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-accent-purple">{i + 1}</span>
                    </div>
                    <span className="text-body-text">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case: Musik i Gentofte */}
      <section className="py-20 md:py-28">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm font-medium text-accent-purple mb-3 uppercase tracking-wide">Case</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Musik i Gentofte 2026
              </h2>
              <p className="text-lg text-body-text leading-relaxed mb-6">
                Musik i Gentofte bruger MUSEIQ som betalingsplatform til festivalen i 2026, hvor platformen håndterer betalinger på tværs af festivalens salgssteder og giver arrangørerne overblik over salget.
              </p>
              <div className="space-y-3">
                {['POS betaling ved alle barer', 'Fast Track bestilling via app', 'Live salgsdata under festivalen'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent-purple flex-shrink-0" />
                    <span className="text-body-text">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <img src="/mig-logo.png" alt="Musik i Gentofte" className="h-16 w-auto rounded-lg" />
              </div>
            </div>
            <div>
              <div className="rounded-2xl overflow-hidden shadow-large">
                <img
                  src="/musikigentofte.jpg"
                  alt="Musik i Gentofte"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section-surface py-20 md:py-24">
        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto text-center text-white">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Klar til at skalere din festival?
            </h2>
            <p className="text-white/85 mb-8">
              Book en demo og se hvordan MUSEIQ kan hjælpe med at afvikle jeres festival.
            </p>
            <Button
              data-testid="festivals-cta"
              size="lg"
              className="cta-button-neutral group"
              onClick={() => {
                const subject = encodeURIComponent("Anmodning om demo - MUSEIQ");
                const body = encodeURIComponent("Hej MuseIQ\n\nJeg vil gerne booke en demo af jeres løsning.\n\nI må meget gerne kontakte mig for at aftale et tidspunkt.\n\nBedste hilsner\n[Navn]\n[Virksomhed]");
                window.location.href = `mailto:Kasper@museiq.io?subject=${subject}&body=${body}`;
              }}
            >
              Book demo
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FestivalsPage;
